import { Voucher } from '@flipdish/api-client-typescript';
import moment from 'moment-timezone';

import { flagService } from '../../../services/flagService';
import { getVoucherStoreTimeZone } from '../selectors';

const DEFAULT_CAN_BE_USED = 'onlyOnce';
export function getCanBeUsed(voucher?: Voucher) {
  if (!voucher) {
    return DEFAULT_CAN_BE_USED;
  }
  // Legacy boolean
  if (voucher.IsValidOnlyOnce) {
    return DEFAULT_CAN_BE_USED;
  }
  // Set IsValidOncePerCustomer to true and MaxRedemptions to 1 is the same as onlyOnce
  if (voucher.IsValidOncePerCustomer && voucher.MaxRedemptions === 1) {
    return DEFAULT_CAN_BE_USED;
  }
  if (voucher.IsValidOncePerCustomer && voucher.IsValidForFirstOrderOnly) {
    return 'oncePerCustomerOnTheirFirstOrderOnly';
  }
  if (voucher.IsValidOncePerCustomer) {
    return 'oncePerCustomer';
  }
  // For backwards compatibility, if IsValidForFirstOrderOnly is true and IsValidOncePerCustomer is false, return oncePerCustomerOnTheirFirstOrderOnly
  if (voucher.IsValidForFirstOrderOnly) {
    return 'oncePerCustomerOnTheirFirstOrderOnly';
  }

  return 'multipleTimes';
}

const DEFAULT_VALID_TYPE = 'delivery & collection';
export function getValidType(voucher?: Voucher) {
  if (!voucher) {
    return DEFAULT_VALID_TYPE;
  }
  if (voucher.IsValidForDeliveryOrders) {
    if (voucher.IsValidForPickupOrders) {
      return 'delivery & collection';
    } else {
      return 'delivery';
    }
  } else {
    return 'collection';
  }
}

const DEFAULT_VALID_PAYMENT = 'pay online & cash';
export function getValidPayment(voucher?: Voucher) {
  if (!voucher) {
    return DEFAULT_VALID_PAYMENT;
  }
  if (voucher.IsValidForOrdersPayedByCash) {
    if (voucher.IsValidForOrdersPayedOnline) {
      return 'pay online & cash';
    } else {
      return 'cash';
    }
  } else {
    return 'pay online';
  }
}

// "Never expires" is stored as a year-2100 date. It's authored as browser-local midnight, so it
// still reads as 2099 in UTC for browsers east of UTC — check both frames or the label is lost.
export const NEVER_EXPIRES_YEAR = 2100;

export function isNeverExpires(date: moment.MomentInput): boolean {
  return moment.utc(date).year() >= NEVER_EXPIRES_YEAR || moment(date).year() >= NEVER_EXPIRES_YEAR;
}

// Shared by the voucher summary and the date dialog so the two can't render the day differently.
// Pass storeTimeZone for API instants; omit it for values already on the day the user picked.
export function formatValidDatesRange(
  start: moment.MomentInput,
  expiry: moment.MomentInput | undefined,
  neverExpiresLabel: string,
  storeTimeZone?: string
): string {
  if (!start) {
    return '';
  }

  const formatDate = (date: moment.MomentInput) =>
    storeTimeZone ? moment.utc(date).tz(storeTimeZone).format('L') : moment(date).format('L');

  const expiryText = !expiry || isNeverExpires(expiry) ? neverExpiresLabel : formatDate(expiry);

  return `${formatDate(start)} - ${expiryText}`;
}

// neverExpiresLabel is passed in rather than translated here so this stays free of i18n, and so the
// summary matches the label IsValidField shows for the same voucher.
export function getValidDates(voucher: Voucher, neverExpiresLabel: string, storeTimeZone?: string) {
  return formatValidDatesRange(
    voucher.StartDate,
    voucher.ExpiryDate,
    neverExpiresLabel,
    storeTimeZone
  );
}

// boundary: 'start' -> store-local 00:00:00, 'end' -> store-local 23:59:59, returned as a UTC ISO string.
export function toStoreLocalDayBoundaryUtc(
  date: moment.MomentInput,
  storeTimeZone: string,
  boundary: 'start' | 'end'
) {
  const storeLocalDate = moment.tz(date, storeTimeZone);
  return (boundary === 'start' ? storeLocalDate.startOf('day') : storeLocalDate.endOf('day'))
    .utc()
    .format();
}

// The calendar day a value already represents, read in whatever zone it carries. Used in place of
// moment.tz(date, zone), which re-expresses the instant and so shifts the day whenever the value's
// own zone and the store's disagree.
export function extractCalendarDay(date: moment.MomentInput): string {
  return moment(date).format('YYYY-MM-DD');
}

// Anchors an existing voucher's date to the store's zone so the pickers and summary show the store's
// day rather than the viewer's.
//
// Returns a moment, not a Date, and that matters twice: the zone anchor is what makes the day come out
// right, and the underlying instant stays intact so applyVoucherDateBoundaries can still recognise an
// untouched date even when the store hadn't loaded yet and no anchor was possible. Calling .toDate()
// here would collapse it to a plain calendar day and lose both.
export function getInitialVoucherDate(
  existingDate: Date | string | undefined,
  storeTimeZone: string | undefined,
  fallback: moment.Moment
): moment.Moment {
  if (!existingDate) {
    return fallback;
  }
  return storeTimeZone ? moment.tz(existingDate, storeTimeZone) : moment(existingDate);
}

// The API types these as Date, but form state holds the anchored moments getInitialVoucherDate
// returns, and state loaded from an update response holds raw strings. All three arrive here.
type VoucherDateValue = Date | string | moment.Moment;

type VoucherDates = {
  StartDate?: VoucherDateValue;
  ExpiryDate?: VoucherDateValue;
  Stores?: number[];
  // Set by StoresSelectField from the store data it already has. Preferred over the ORM lookup, which
  // is empty on the create screen — nothing there loads stores into it.
  TimeZone?: string;
};

// The day a server instant falls on in the store's zone. Parsed as UTC because server dates are UTC
// but carry no offset marker, and not every path parses them (UPDATE_VOUCHER_SUCCESS stores the
// response as-is) — moment.tz would read a bare "2026-07-25T04:59:59" as store wall-clock time and
// land a day late.
function storeLocalDayOfInstant(date: moment.MomentInput, storeTimeZone: string): string {
  return moment.utc(date).tz(storeTimeZone).format('YYYY-MM-DD');
}

// Compared as UTC so a bare server string and a parsed Date for the same instant still match.
function isSameInstant(a: moment.MomentInput, b: moment.MomentInput): boolean {
  return moment.utc(a).valueOf() === moment.utc(b).valueOf();
}

export type ApplyVoucherDateBoundariesOptions = {
  /** The server's copy of this voucher, when there is one. */
  originalVoucher?: VoucherDates;
  /**
   * Only used when the flag is off, where it reproduces what each caller did before: the voucher forms
   * applied browser-local day boundaries, whole-voucher updates from VoucherDetails left dates alone.
   * Without it a plain enable/disable toggle would start rewriting expiry times.
   *
   * TODO - remove when expiryUsesStoreTimezone is fully released
   */
  expiryUsesStoreTimezoneLegacyDayBoundaries?: boolean;
};

/**
 * Converts a voucher's dates into the instants the API stores. Called from vouchersService's create
 * and updateVoucher, so every write path — the four forms, VoucherDetails and the bulk importer —
 * goes through it.
 *
 * Dates reach here from two sources that need opposite handling and can't be told apart by looking at
 * them: server instants, whose intended day is the day in the store's zone, and days the user picked,
 * whose intended day is the one that was on screen. `originalVoucher` distinguishes them — a date
 * still equal to the server's instant was never touched.
 *
 * That makes the conversion idempotent, so re-saving an unchanged voucher doesn't walk its expiry
 * forward, and it stays correct whether or not the store had loaded when the form mounted.
 */
export function applyVoucherDateBoundaries<T extends VoucherDates>(
  voucher: T,
  state: unknown,
  {
    originalVoucher,
    expiryUsesStoreTimezoneLegacyDayBoundaries = true,
  }: ApplyVoucherDateBoundariesOptions = {}
): T {
  const storeTimeZone = flagService.isFlagOn(state, 'expiryUsesStoreTimezone')
    ? voucher.TimeZone || getVoucherStoreTimeZone(state as AppState, voucher.Stores)
    : undefined;

  const convert = (
    date: VoucherDateValue | undefined,
    originalDate: VoucherDateValue | undefined,
    boundary: 'start' | 'end'
  ) => {
    // moment(undefined) is "now", which would invent a date that was never set.
    if (date === undefined || date === null) {
      return date;
    }

    if (!storeTimeZone) {
      if (!expiryUsesStoreTimezoneLegacyDayBoundaries) {
        return date;
      }
      const ambient = moment(date);
      return (boundary === 'start'
        ? ambient.startOf('day')
        : ambient.endOf('day')
      ).format() as unknown as Date;
    }

    const day =
      originalDate && isSameInstant(date, originalDate)
        ? storeLocalDayOfInstant(originalDate, storeTimeZone)
        : extractCalendarDay(date);

    return toStoreLocalDayBoundaryUtc(day, storeTimeZone, boundary) as unknown as Date;
  };

  return {
    ...voucher,
    StartDate: convert(voucher.StartDate, originalVoucher?.StartDate, 'start'),
    ExpiryDate: convert(voucher.ExpiryDate, originalVoucher?.ExpiryDate, 'end'),
  };
}
